import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { bookingService } from '@/api/services/bookingService'
import { getApiError } from '@/api/client'
import { useChangeTrainer } from '@/api/hooks'
import { toast } from '@/store/useToastStore'
import { Modal } from '@/shared/ui/Modal'
import { Textarea } from '@/shared/ui/Input'
import { Avatar } from '@/shared/ui/Avatar'
import { cn, localized } from '@/shared/utils'
import { AlertTriangle, Search, Check, ChevronDown, MapPin, Car, ArrowRight, UserRound, Wallet, ArrowLeftRight } from 'lucide-react'

interface TrainerOption {
  user_id: number; name: string; name_ar?: string; name_ur?: string
  photo_url?: string | null
  // Distance/price if this trainer is picked, vs. the current trainer — the API computes both sides per row.
  old_distance?: number | null; new_distance?: number | null; distance_diff?: number | null
  old_price?: number | null; new_price?: number | null; price_diff?: number | null
  is_pickup_enabled?: boolean | null
}

function TrainerMeta({ tr, className }: { tr: TrainerOption; className?: string }) {
  return (
    <span className={cn('flex items-center gap-2.5 flex-wrap text-[11px] text-gray-400', className)}>
      {tr.new_distance != null && (
        <span className="flex items-center gap-0.5"><MapPin size={10} />{tr.new_distance.toFixed(1)} km</span>
      )}
      {tr.new_price != null && (
        <span className="flex items-center gap-0.5"><Wallet size={10} />AED {tr.new_price.toFixed(2)}</span>
      )}
      {tr.is_pickup_enabled && <span className="flex items-center gap-0.5 text-green-500"><Car size={10} />Pickup</span>}
    </span>
  )
}

// Searchable trainer dropdown — a native <select> doesn't support filtering, and schools
// can have long trainer rosters, so this is a small combobox scoped to this modal.
function TrainerSearchSelect({ trainers, loading, value, onChange }: {
  trainers: TrainerOption[]; loading: boolean; value: string; onChange: (userId: string) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const selected = trainers.find(tr => String(tr.user_id) === value)
  const displayName = (tr: TrainerOption) => localized(tr, 'name', i18n.language)
  const filtered = trainers.filter(tr => tr.name?.toLowerCase().includes(search.trim().toLowerCase()))

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const pick = (tr: TrainerOption) => { onChange(String(tr.user_id)); setSearch(''); setOpen(false) }

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('changeTrainerModal.newTrainer')}</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          disabled={loading}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
        >
          {selected && <Avatar name={displayName(selected)} src={selected.photo_url} size="xs" />}
          <span className={cn('flex-1 text-start min-w-0', !selected && 'text-gray-400')}>
            <span className="block truncate">
              {loading ? t('changeTrainerModal.loadingTrainers') : (selected ? displayName(selected) : t('changeTrainerModal.selectTrainer'))}
            </span>
            {selected && <TrainerMeta tr={selected} className="mt-0.5" />}
          </span>
          <ChevronDown size={14} className={cn('text-gray-400 flex-shrink-0 transition-transform', open && 'rotate-180')} />
        </button>

        {open && (
          <div className="absolute z-20 top-full start-0 mt-1 w-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
              <Search size={13} className="text-gray-400 flex-shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('changeTrainerModal.searchTrainers')}
                className="flex-1 text-sm focus:outline-none placeholder:text-gray-400"
              />
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">{t('changeTrainerModal.noTrainersFound')}</div>
              ) : filtered.map(tr => (
                <button
                  key={tr.user_id}
                  type="button"
                  onClick={() => pick(tr)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-start hover:bg-brand-50 transition-colors',
                    String(tr.user_id) === value ? 'bg-brand-50/60' : ''
                  )}
                >
                  <Avatar name={displayName(tr)} src={tr.photo_url} size="sm" />
                  <span className="flex-1 min-w-0">
                    <span className={cn('flex items-center gap-1.5 truncate', String(tr.user_id) === value ? 'text-brand-600 font-semibold' : 'text-gray-700')}>
                      <span className="truncate">{displayName(tr)}</span>
                      {String(tr.user_id) === value && <Check size={13} className="text-brand-500 flex-shrink-0" />}
                    </span>
                    <TrainerMeta tr={tr} className="mt-0.5" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// A single stat tile in the app's established solid-color style (see BookingDetailsPage's
// payment summary tiles) — the new value front and center, the old value as context underneath.
function DeltaTile({ icon, label, oldVal, newVal, diff, formatDiff, active }: {
  icon: React.ReactNode; label: string; oldVal: string; newVal: string
  diff: number | null; formatDiff: (n: number) => string; active: boolean
}) {
  const { t } = useTranslation()
  const bg = !active ? 'bg-gray-100 text-gray-400'
    : diff == null || diff === 0 ? 'bg-brand-500 text-white'
    : diff < 0 ? 'bg-green-500 text-white'
    : 'bg-red-500 text-white'
  return (
    <div className={cn('rounded-xl p-4', bg)}>
      <div className="flex items-center justify-between mb-2">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', active ? 'bg-white/20' : 'bg-white')}>{icon}</div>
        {active && diff != null && diff !== 0 && (
          <span className="text-[10px] font-bold bg-white/25 rounded-full px-2 py-0.5">{diff > 0 ? '+' : ''}{formatDiff(diff)}</span>
        )}
      </div>
      <div className="text-[11px] font-semibold uppercase opacity-80">{label}</div>
      <div className="text-lg font-black mt-0.5">{newVal}</div>
      <div className="text-[11px] font-medium opacity-75 mt-0.5">{t('changeTrainerModal.wasLabel')} {oldVal}</div>
    </div>
  )
}

// Current trainer vs. selected candidate: avatars up top, distance + pickup price compared below —
// styled to match the rest of the booking page (colored header bar, solid stat tiles).
function TrainerCompare({ oldName, oldPhoto, newTrainer, newName, oldDistance, oldPrice }: {
  oldName: string; oldPhoto?: string | null
  newTrainer: TrainerOption | null; newName: string | null
  oldDistance: number | null; oldPrice: number | null
}) {
  const { t } = useTranslation()
  const newDistance = newTrainer?.new_distance ?? null
  const newPrice = newTrainer?.new_price ?? null

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-brand-500 flex items-center gap-2">
        <ArrowLeftRight size={14} className="text-white" />
        <span className="text-sm font-bold text-white">{t('changeTrainerModal.compareHeading')}</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-center gap-4">
          <div className="flex flex-col items-center gap-1.5 w-28">
            <Avatar name={oldName} src={oldPhoto} size="lg" />
            <span className="text-xs font-bold text-gray-800 truncate max-w-full">{oldName}</span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{t('changeTrainerModal.currentTrainerLabel')}</span>
          </div>

          <ArrowRight size={20} className="text-gray-300 flex-shrink-0" />

          <div className="flex flex-col items-center gap-1.5 w-28">
            {newTrainer ? (
              <Avatar name={newName ?? ''} src={newTrainer.photo_url} size="lg" />
            ) : (
              <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                <UserRound size={18} />
              </div>
            )}
            <span className={cn('text-xs font-bold truncate max-w-full', newTrainer ? 'text-brand-700' : 'text-gray-400')}>
              {newName ?? t('changeTrainerModal.selectTrainer')}
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{t('changeTrainerModal.newTrainer')}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DeltaTile
            icon={<MapPin size={16} />} label={t('changeTrainerModal.distanceLabel')} active={!!newTrainer}
            oldVal={oldDistance != null ? `${oldDistance.toFixed(1)} km` : '—'}
            newVal={newTrainer ? (newDistance != null ? `${newDistance.toFixed(1)} km` : '—') : (oldDistance != null ? `${oldDistance.toFixed(1)} km` : '—')}
            diff={newTrainer?.distance_diff ?? null}
            formatDiff={(n) => `${n.toFixed(1)} km`}
          />
          <DeltaTile
            icon={<Wallet size={16} />} label={t('changeTrainerModal.priceLabel')} active={!!newTrainer}
            oldVal={oldPrice != null ? `AED ${oldPrice.toFixed(2)}` : '—'}
            newVal={newTrainer ? (newPrice != null ? `AED ${newPrice.toFixed(2)}` : '—') : (oldPrice != null ? `AED ${oldPrice.toFixed(2)}` : '—')}
            diff={newTrainer?.price_diff ?? null}
            formatDiff={(n) => `AED ${n.toFixed(2)}`}
          />
        </div>
      </div>
    </div>
  )
}

// Reassign a booking to a new trainer on behalf of the school:
// POST admin/booking/trainer-changing/change-free { booking_id, new_trainer_id, reason }
export function ChangeTrainerModal({ open, onClose, booking }: { open: boolean; onClose: () => void; booking: any }) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const [trainerId, setTrainerId] = useState('')
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)
  const currentTrainerId = booking.driver?.id ?? booking.driver_id
  const currentTrainerName = localized(booking.driver, 'name', i18n.language)

  // Eligible replacement trainers are scoped server-side to this booking's
  // school via booking_id — no need to derive vendor_id on the client.
  const { data: trainersData, isLoading: trainersLoading, isError: trainersError, error: trainersErrorObj } = useQuery({
    queryKey: ['booking-trainers-for-change', booking.id],
    queryFn: () => bookingService.changeTrainerList(booking.id),
    enabled: open && !!booking.id,
    retry: false,
  })
  // Support both a flat row shape (`tr.id`) and a nested one (`tr.user.id`).
  const trainers: TrainerOption[] = ((trainersData as any)?.data ?? [])
    .map((tr: any) => ({
      user_id: tr.user?.id ?? tr.user_id ?? tr.id,
      name: tr.user?.name ?? tr.name,
      name_ar: tr.user?.name_ar ?? tr.name_ar,
      name_ur: tr.user?.name_ur ?? tr.name_ur,
      photo_url: tr.user?.photo_url ?? tr.photo_url,
      old_distance: tr.old_distance != null ? Number(tr.old_distance) : null,
      new_distance: tr.new_distance != null ? Number(tr.new_distance) : null,
      distance_diff: tr.difference_between_distance != null ? Number(tr.difference_between_distance) : null,
      old_price: tr.old_pickup_price != null ? Number(tr.old_pickup_price) : null,
      new_price: tr.new_pickup_price != null ? Number(tr.new_pickup_price) : null,
      price_diff: tr.difference_between_pickup_price != null ? Number(tr.difference_between_pickup_price) : null,
      is_pickup_enabled: tr.is_pickup_enabled ?? null,
    }))
    .filter((tr: TrainerOption) => tr.user_id != null && tr.user_id !== currentTrainerId)
    // Nearest new distance first.
    .sort((a: TrainerOption, b: TrainerOption) => (a.new_distance ?? Infinity) - (b.new_distance ?? Infinity))

  // The current trainer's own distance/price come back identically on every row.
  const oldDistance = trainers[0]?.old_distance ?? null
  const oldPrice = trainers[0]?.old_price ?? null

  const selectedTrainer = trainers.find((tr) => String(tr.user_id) === trainerId)
  const selectedTrainerName = selectedTrainer ? localized(selectedTrainer, 'name', i18n.language) : null

  const changeTrainer = useChangeTrainer()

  const handleClose = () => { setTrainerId(''); setReason(''); setConfirming(false); onClose() }

  const requestConfirm = () => {
    if (!trainerId) { toast.error(t('changeTrainerModal.toastSelectTrainer')); return }
    if (!reason.trim()) { toast.error(t('changeTrainerModal.toastEnterReason')); return }
    setConfirming(true)
  }

  const confirmSubmit = () => {
    changeTrainer.mutate(
      { booking_id: booking.id, new_trainer_id: Number(trainerId), reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(t('changeTrainerModal.toastSuccess'))
          qc.invalidateQueries({ queryKey: ['booking-detail', String(booking.id)] })
          qc.invalidateQueries({ queryKey: ['bookings'] })
          handleClose()
        },
        onError: (e: any) => { toast.error(e?.response?.data?.message ?? t('changeTrainerModal.toastFail')); setConfirming(false) },
      },
    )
  }

  return (
    <Modal open={open} onClose={handleClose} title={confirming ? t('changeTrainerModal.confirmTitle') : t('changeTrainerModal.title')} size="md">
      {confirming ? (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{t('changeTrainerModal.bookingHash')}{booking.id}</p>
          <TrainerCompare
            oldName={currentTrainerName} oldPhoto={booking.driver?.photo_url}
            newTrainer={selectedTrainer ?? null} newName={selectedTrainerName}
            oldDistance={oldDistance} oldPrice={oldPrice}
          />
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('changeTrainerModal.reason')}</div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{reason.trim()}</p>
          </div>
          <div className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">{t('changeTrainerModal.notifiesWarning')}</p>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setConfirming(false)}
              disabled={changeTrainer.isPending}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {t('changeTrainerModal.back')}
            </button>
            <button
              onClick={confirmSubmit}
              disabled={changeTrainer.isPending}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
            >
              {changeTrainer.isPending ? t('changeTrainerModal.pleaseWait') : t('changeTrainerModal.yesChangeTrainer')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{t('changeTrainerModal.bookingHash')}{booking.id}</p>
          <TrainerCompare
            oldName={currentTrainerName} oldPhoto={booking.driver?.photo_url}
            newTrainer={selectedTrainer ?? null} newName={selectedTrainerName}
            oldDistance={oldDistance} oldPrice={oldPrice}
          />
          {trainersError ? (
            <div className="flex gap-3 p-3 rounded-xl bg-red-50 border border-red-100">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{getApiError(trainersErrorObj)}</p>
            </div>
          ) : (
            <>
              <TrainerSearchSelect trainers={trainers} loading={trainersLoading} value={trainerId} onChange={setTrainerId} />
              <Textarea
                label={t('changeTrainerModal.reason')}
                className="min-h-24"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('changeTrainerModal.reasonPlaceholder')}
              />
            </>
          )}
          <div className="flex items-center justify-end gap-3">
            <button onClick={handleClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('changeTrainerModal.cancel')}</button>
            {!trainersError && (
              <button
                onClick={requestConfirm}
                disabled={!trainerId || !reason.trim()}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
              >
                {t('changeTrainerModal.changeTrainer')}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
